import { endTour, setStepsRaw, setTourStep, startTour } from "@store/slices/tourSlice.tsx";
import { Tour } from "antd";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "@store/store.tsx";
import { StepRaw, TourStep } from "@app-types/tour.types.ts";
import { logInfo } from "@tools/LogsHandler.tsx";
import { waitForElementToBeVisible, waitForScrollToComplete } from "@tools/utils/common.tsx";
import {
    getStep,
    getTargetElement,
    getValidTourSteps,
    scrollToFirstElement,
    transformToTourSteps
} from "@eshop/components/tours/Utils.tsx";
import { tourFunctions } from "@eshop/components/tours/tourFunctions.tsx";

export const RenderTour: React.FC<{ tourSteps: TourStep[], tourName: string }> = ({ tourSteps, tourName }) => {
    const dispatch = useAppDispatch();
    const { isTourOpen, currentStep, steps } = useSelector((state: RootState) => state.tour);
    const [tourStarted, setTourStarted] = useState(false); // Estado para controlar si el tour ya ha comenzado

    useEffect(() => {
        // Usar una clave única en localStorage para cada tour según el tourName
        const tourCompletedKey = `tourCompleted_${tourName}`;
        const isTourCompleted = localStorage.getItem(tourCompletedKey);

        if (tourStarted) return; // Si el tour ya ha comenzado, no vuelvas a ejecutar el efecto

        const observer = new MutationObserver(() => {
            const validSteps = getValidTourSteps(tourSteps);

            if (validSteps && validSteps.length > 0) {
                // Todos los pasos obligatorios tienen un target asociado
                // logInfo('::::: validSteps: ', validSteps);
                const stepsRaw: StepRaw = {
                    tourName: tourName,
                    stepsRaw: tourSteps,
                }

                const startTourAsync = async () => {
                    if (isTourCompleted) {
                        dispatch(setStepsRaw(stepsRaw));
                    } else {
                        // Llamada a la función async para hacer scroll al primer elemento
                        await scrollToFirstElement(validSteps);
                        dispatch(startTour({ steps: validSteps, stepsRawList: stepsRaw }));
                        setTourStarted(true); // Marcar que el tour ha comenzado
                        localStorage.setItem(tourCompletedKey, 'true'); // Guardar en localStorage que el tour fue completado
                    }
                };

                startTourAsync().catch(error => {
                    console.error('Error al iniciar el tour:', error);
                });

                observer.disconnect(); // Desconectar el observer una vez que el tour ha comenzado
            }
            // else {
            //     logInfo('Algunos pasos obligatorios no tienen elementos asociados');
            // }
        });

        observer.observe(document.body, { childList: true, subtree: true });

        return () => observer.disconnect();
    }, []);  // eslint-disable-line react-hooks/exhaustive-deps

    const handleTourChange = async (current: number) => {
        // Verificar si current supera el número de pasos
        if (current >= steps.length) {
            logInfo('El tour ha alcanzado o superado el último paso. Finalizando el tour.');
            dispatch(endTour());  // Finalizar el tour
            return;  // Salir de la función
        }

        const step = getStep(current, steps);
        logInfo('--------- Ingreso a RenderTour - handleTourChange current: ', current, 'step: ', step);

        if (step?.executeBefore) {
            const executeBefore = tourFunctions[step.executeBefore];
            if (executeBefore) {
                await executeBefore();
            }
        }

        const element = getTargetElement(step);
        if (element) {
            // Hacer scroll para centrar el elemento en la vista si está fuera del área visible
            await waitForScrollToComplete(element);

            if (step.onlyVisible) {
                await waitForElementToBeVisible({ element, fullyInView: step.fullyInView });  // Esperar hasta que el elemento sea visible
            }
        } else if (!element && step.optional && step.target.allowHidden) {
            // Si no se encuentra el elemento y el paso es opcional y permite oculto, saltar al siguiente
            logInfo(`Paso ${current} es opcional y no se encuentra el elemento, saltando al siguiente - step: `, step);
            if (current > currentStep) {
                await handleTourChange(current + 1);  // Pasar al siguiente paso
            } else {
                await handleTourChange(current - 1);  // Pasar al siguiente paso
            }
            return;  // Salir de la función
        }

        dispatch(setTourStep(current));  // Dispatch para cambiar al siguiente paso
    };

    return (isTourOpen && (
        <Tour
            current={currentStep}
            steps={transformToTourSteps(steps)}
            onChange={handleTourChange}
            onClose={() => dispatch(endTour())}
        />
    )
    );
};
